import React, { useState, useEffect, useRef, useCallback } from 'react';

const GRAVITY = 0.6;
const JUMP = -12;
const GROUND = 220;
const SPEED = 4;

const CHARACTERS = [
  { id: 'mono', emoji: '🐒', name: 'Monito' },
  { id: 'gorila', emoji: '🦍', name: 'Gorila Ape' },
];

const WORLDS = [
  { id: 'selva', name: '🌴 Selva', sky: '#dcfce7', ground: '#166534', obstacle: '🌵' },
  { id: 'ciudad', name: '🏙️ Ciudad', sky: '#e8f0fe', ground: '#1b2938', obstacle: '🚧' },
  { id: 'pista', name: '🏎️ Pista', sky: '#fef3c7', ground: '#374151', obstacle: '🚗' },
];

export default function MonkeyGame() {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(parseInt(localStorage.getItem('monkey_highscore') || '0'));
  const [gameOver, setGameOver] = useState(false);
  const [started, setStarted] = useState(false);
  const [character, setCharacter] = useState(CHARACTERS[0]);
  const [world, setWorld] = useState(WORLDS[0]);
  const [showMenu, setShowMenu] = useState(true);
  const gameRef = useRef({ y: GROUND, vy: 0, obstacles: [], bananas: [], frame: 0, score: 0, running: false });
  const audioCtx = useRef(null);

  const playSound = useCallback((type) => {
    try {
      if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtx.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.value = 0.1;

      if (type === 'jump') {
        osc.frequency.setValueAtTime(400, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.1);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
        osc.start(); osc.stop(ctx.currentTime + 0.15);
      } else if (type === 'coin') {
        osc.frequency.setValueAtTime(800, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
        osc.start(); osc.stop(ctx.currentTime + 0.2);
      } else if (type === 'crash') {
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(200, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.3);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        osc.start(); osc.stop(ctx.currentTime + 0.4);
      }
    } catch {}
  }, []);

  const reset = useCallback(() => {
    const g = gameRef.current;
    g.y = GROUND; g.vy = 0; g.obstacles = []; g.bananas = []; g.frame = 0; g.score = 0; g.running = true;
    setScore(0); setGameOver(false); setStarted(true); setShowMenu(false);
  }, []);

  const jump = useCallback(() => {
    const g = gameRef.current;
    if (!g.running) { reset(); return; }
    if (g.y >= GROUND - 2) { g.vy = JUMP; playSound('jump'); }
  }, [reset, playSound]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const loop = () => {
      const g = gameRef.current;
      const w = world;
      const ch = character;

      ctx.clearRect(0, 0, 400, 260);
      ctx.fillStyle = w.sky;
      ctx.fillRect(0, 0, 400, 260);
      ctx.fillStyle = w.ground;
      ctx.fillRect(0, 240, 400, 20);

      // Detalles del mundo
      if (w.id === 'pista') {
        ctx.strokeStyle = '#fbbf24';
        ctx.setLineDash([20, 15]);
        ctx.beginPath();
        ctx.moveTo(0, 250);
        ctx.lineTo(400, 250);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      if (!g.running) { animId = requestAnimationFrame(loop); return; }

      // Character
      g.vy += GRAVITY;
      g.y = Math.min(g.y + g.vy, GROUND);
      ctx.font = '28px serif';
      ctx.fillText(ch.emoji, 50, g.y);

      // Obstacles
      if (g.frame % 80 === 0) g.obstacles.push({ x: 420 });
      g.obstacles = g.obstacles.filter(o => o.x > -30);
      g.obstacles.forEach(o => {
        o.x -= SPEED + g.frame * 0.001;
        ctx.font = '26px serif';
        ctx.fillText(w.obstacle, o.x, 235);
        if (o.x < 72 && o.x + 20 > 50 && g.y > 200) {
          g.running = false;
          setGameOver(true);
          playSound('crash');
          if (g.score > highScore) {
            setHighScore(g.score);
            localStorage.setItem('monkey_highscore', String(g.score));
          }
          // Guardar score
          try {
            const msisdn = JSON.parse(localStorage.getItem('formData') || '{}').msisdn || 'anon';
            fetch(`${process.env.REACT_APP_API_URL}/prod/game_scores`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ action: 'save', msisdn, personaje: ch.id, mundo: w.id, score: g.score }),
            });
          } catch {}
        }
      });

      // Bananas
      if (g.frame % 50 === 25) g.bananas.push({ x: 420, y: 170 + Math.random() * 40 });
      g.bananas = g.bananas.filter(b => b.x > -20);
      g.bananas.forEach((b, i) => {
        b.x -= SPEED + g.frame * 0.001;
        ctx.font = '20px serif';
        ctx.fillText('🍌', b.x, b.y);
        if (b.x < 72 && b.x > 35 && Math.abs(g.y - b.y) < 28) {
          g.bananas.splice(i, 1);
          g.score += 10;
          setScore(g.score);
          playSound('coin');
        }
      });

      // Score
      ctx.fillStyle = '#1b2938';
      ctx.font = 'bold 14px Arial';
      ctx.fillText(`🍌 ${g.score}`, 330, 20);

      g.frame++;
      animId = requestAnimationFrame(loop);
    };

    animId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animId);
  }, [highScore, world, character]);

  const btnStyle = (selected) => ({
    padding: '12px 16px', borderRadius: '12px', border: selected ? '2px solid #1b2938' : '1px solid #e2e8f0',
    background: selected ? '#1b2938' : '#fff', color: selected ? '#fff' : '#1b2938',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600', textAlign: 'center', flex: 1,
  });

  return (
    <div style={{ marginTop: 10, textAlign: 'center', paddingBottom: 80 }}>
      <h2 style={{ margin: '0 0 12px 0' }}>🎮 Monkey Runner</h2>

      {showMenu && (
        <div style={{ maxWidth: 400, margin: '0 auto 14px', textAlign: 'left' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Personaje:</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {CHARACTERS.map(c => (
              <button key={c.id} onClick={() => setCharacter(c)} style={btnStyle(character.id === c.id)}>
                <span style={{ fontSize: 22 }}>{c.emoji}</span> {c.name}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Mundo:</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {WORLDS.map(w => (
              <button key={w.id} onClick={() => setWorld(w)} style={btnStyle(world.id === w.id)}>
                {w.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ position: 'relative', display: 'inline-block' }}>
        <canvas
          ref={canvasRef}
          width={400}
          height={260}
          onClick={jump}
          onTouchStart={(e) => { e.preventDefault(); jump(); }}
          style={{
            border: '2px solid #1b2938', borderRadius: 14, cursor: 'pointer',
            width: '100%', height: 'auto', touchAction: 'none',
          }}
        />
        {!started && (
          <div onClick={() => { reset(); }} onTouchStart={(e) => { e.preventDefault(); reset(); }} style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(27,41,56,0.8)', borderRadius: 14, color: '#fff', cursor: 'pointer',
          }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>{character.emoji}</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Toca para jugar</div>
          </div>
        )}
        {gameOver && (
          <div onClick={jump} onTouchStart={(e) => { e.preventDefault(); jump(); }} style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(27,41,56,0.85)', borderRadius: 14, color: '#fff', cursor: 'pointer',
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Game Over</div>
            <div style={{ fontSize: 26, fontWeight: 800 }}>🍌 {score}</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Record: {highScore}</div>
            <div style={{ fontSize: 13, marginTop: 10, color: '#22c55e' }}>Toca para reintentar</div>
            <div style={{ fontSize: 11, marginTop: 4, color: '#9ca3af' }} onClick={(e) => { e.stopPropagation(); setShowMenu(true); setStarted(false); setGameOver(false); }}>
              Cambiar personaje/mundo
            </div>
          </div>
        )}
      </div>

      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
        Record: 🍌 {highScore}
      </div>
    </div>
  );
}
